import { useEffect, useState } from "react";
import { Table } from "react-bootstrap";
import CommonModal from "../shared/modalLayout/CommonModal";
import Pagination from "../shared/pagination/Pagination";
import Loader from "../shared/loader/Loader";
import Button from "../shared/buttons/button";
import { useGetVaPayoutLogActionsQuery } from "../../app/vaApi/vaApi";
import { formatDashboardDateTime } from "../../helpers/dashboardTime";

const PAGE_SIZE = 10;

const SKIPPED_REASON_LABELS = {
  order_cancelled: "Order cancelled — commission not paid",
  order_not_shipped: "Order not shipped yet — commission held",
  order_not_delivered: "Order not shipped yet — commission held",
  missing_connect_account: "Stripe Connect account missing",
  zero_amount: "Amount is zero",
  no_pending_bills: "No pending bills",
  no_bills_in_payout_period: "No bills in payout period",
};

function escapeHtml(value) {
  return String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function formatCurrency(amount) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(amount) || 0);
}

function formatDateTime(value) {
  return formatDashboardDateTime(value, "—");
}

function historyStatus(item) {
  const reason = String(item?.skippedReason || "").trim();
  if (reason === "order_cancelled") return "Cancelled";
  const raw = String(item?.status || "").trim().toLowerCase();
  if (raw === "paid") return "Paid";
  if (raw === "failed") return "Failed";
  if (raw === "pending") return "Pending";
  if (raw === "skipped") return "Skipped";
  return raw ? raw.charAt(0).toUpperCase() + raw.slice(1) : "—";
}

function historyReason(item) {
  const status = String(item?.status || "").trim().toLowerCase();
  if (item?.failureMessage) return item.failureMessage;
  const skip = String(item?.skippedReason || "").trim();
  if (skip) return SKIPPED_REASON_LABELS[skip] || skip.replace(/_/g, " ");
  if (status === "paid") return "Commission paid via Stripe";
  return "—";
}

export default function VaDistributorCommissionHistoryModal({
  show,
  setShow,
  distributor,
}) {
  const [page, setPage] = useState(1);
  const distributorId = distributor?.id;

  useEffect(() => {
    if (show) setPage(1);
  }, [show, distributorId]);

  const { data, isFetching, isError, refetch } = useGetVaPayoutLogActionsQuery(
    {
      distributorId,
      page,
      limit: PAGE_SIZE,
      history: true,
    },
    {
      skip: !show || !distributorId,
      placeholderData: (previousData) => previousData,
    }
  );

  const items = data?.data?.items ?? [];
  const pagination = data?.data?.pagination;
  const total = Number(pagination?.total) || 0;
  const totalPages = Number(pagination?.totalPages) || 0;
  const pageLimit = Number(pagination?.limit) || PAGE_SIZE;
  const start = total === 0 ? 0 : (page - 1) * pageLimit + 1;
  const end = Math.min(page * pageLimit, total);
  const name = distributor?.name || "Distributor";

  return (
    <CommonModal
      show={show}
      setShow={setShow}
      title={`${escapeHtml(name)} — Commission history`}
      centered
      className="modal-lg va-commission-history-modal"
    >
      {isFetching && items.length === 0 ? (
        <div className="py-4 d-flex justify-content-center">
          <Loader />
        </div>
      ) : isError ? (
        <div className="text-center py-4">
          <p className="text-danger small mb-2">Failed to load commission history</p>
          <Button label="Retry" className="btn-grey" onClick={refetch} />
        </div>
      ) : (
        <>
          <div className="va-commission-history-modal__table-wrap">
            <Table hover responsive className="va-commission-history-modal__table mb-0">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Status</th>
                  <th>Amount</th>
                  <th>Bills</th>
                  <th>Reason</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-muted text-center py-4">
                      No commission history yet
                    </td>
                  </tr>
                ) : (
                  items.map((item) => {
                    const status = historyStatus(item);
                    return (
                      <tr key={`${item.logId || item.id}-${item.status}-${item.skippedReason || ""}-${item.stripePayoutId || ""}-${(item.billNumbers || []).join(",")}`}>
                        <td>{formatDateTime(item.startedAt)}</td>
                        <td>
                          <span
                            className={`va-commission-history-modal__badge va-commission-history-modal__badge--${status.toLowerCase()}`}
                          >
                            {status}
                          </span>
                        </td>
                        <td className="va-commission-history-modal__amount">
                          {formatCurrency(item.amount)}
                        </td>
                        <td>
                          {(item.billNumbers || []).length
                            ? item.billNumbers.join(", ")
                            : "—"}
                        </td>
                        <td className="va-commission-history-modal__reason">
                          {historyReason(item)}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </Table>
          </div>

          {(total > 0 || totalPages > 1) && (
            <div className="va-commission-history-modal__footer">
              <span className="text-muted small">
                Showing <strong>{start}–{end}</strong> of <strong>{total}</strong>
              </span>
              {totalPages > 1 && (
                <Pagination
                  currentPage={page}
                  totalPages={totalPages}
                  onPageChange={setPage}
                />
              )}
            </div>
          )}
        </>
      )}
    </CommonModal>
  );
}
